import { get, isEmpty } from 'radash';
import { useCallback } from 'react';

import { useDataModel } from '@/hooks/useDataModel';
import { useDateFormatter } from '@/hooks/useDateFormatter';
import { useOrganization } from '@/hooks/useOrganization';
import { useUser } from '@/hooks/useUser';
import { getCustomUiReferenceFilter } from '@/services/angularjs-bridge';
import { logger } from '@/services/logger';
import { DataModel, DataModelAttribute, DataModelAttributeBaseClass, DataModelAttributeTypeName, PersistedItem } from '@/types/DataModel';

import { useOptionTranslator } from './useOptionTranslator';

const useReferenceItemFormatter = (attribute: DataModelAttribute) => {

    const { user } = useUser();
    const { organization } = useOrganization();
    const { dataModel } = useDataModel();
    const { translateOption } = useOptionTranslator();
    const { formatDate } = useDateFormatter();

    const getFirstDimensionalAttributeKey = (attribute: DataModelAttribute) => {

        if (attribute.typeName !== DataModelAttributeTypeName.Dimensional ||
            !attribute.params ||
            !attribute.params.keys ) {
            logger.error(`useReferenceItemFormatter: Attribute ${attribute.name} does not have dimensional keys.`);
            return '';
        }

        return attribute.params.keys[0];
    };

    const getValue = (dimension: any, value:any) => {
        const key = dimension.key;
        if (!Object.prototype.hasOwnProperty.call(dimension, 'key') || !Object.prototype.hasOwnProperty.call(value, key)) {
            logger.error(`Key "${key}" not found in "value" object`);
            return '';
        }
        return value[key];
    };

    const formatAttributeValue = (item: PersistedItem, filterAttribute: DataModelAttribute): string => {

        if (isEmpty(item)) {
            return '';
        }

        const value = get(item, filterAttribute.name) as string | number | Record<string, string | number>;
        if (isEmpty(value)) {
            return '';
        }

        const type = filterAttribute.typeName || filterAttribute.baseClass;
        switch (type) {

            case DataModelAttributeTypeName.String:
            case DataModelAttributeBaseClass.Integer:
            case DataModelAttributeBaseClass.Float:
            case DataModelAttributeTypeName.Image:
            case DataModelAttributeTypeName.Document:
                return value as string;

            case DataModelAttributeTypeName.Boolean:
            case DataModelAttributeTypeName.Enum:
            case DataModelAttributeTypeName.OpenEnum:
            case DataModelAttributeTypeName.Codelist:
                return translateOption(value as string, filterAttribute);

            case DataModelAttributeTypeName.Date:
                return formatDate(value as string, 'mediumDate');

            case DataModelAttributeTypeName.DateTime:
                return formatDate(value as string, 'medium');

            case DataModelAttributeTypeName.Physical:
                // FIXME: For Physical Attributes there should be a hook that we can
                // use to translate the unit of the physical attribute, the same one
                // that is used in the actual physical attribute component.
                //value = formatPhysicalAttribute(value, attribute);
                return 'translated-physical-value';

            case DataModelAttributeTypeName.Dimensional: {
                const dimension = getFirstDimensionalAttributeKey(filterAttribute);
                return getValue(dimension, value);
            }

            default: {
                logger.warn(`useReferenceItemFormatter: Rendering attribute ${filterAttribute.name} of type ${filterAttribute.typeName} as a dropdown item is not supported.`);
                return '';
            }

        }

    };

    const formatByFilters = (dataModel: DataModel, item: PersistedItem, displayNameFilterAttributes: string[]) => {

        const displayNames: string[] = [];
        displayNameFilterAttributes.forEach(attributeName => {

            const referencedFilterAttribute = dataModel.attribute(attributeName);

            if (!referencedFilterAttribute) {
                logger.warn(`useReferenceItemFormatter: While trying to render attribute ${attributeName} as a dropdown item, it was not found.`);
                return;
            }

            const formattedValue = formatAttributeValue(item, referencedFilterAttribute);
            if (!isEmpty(formattedValue)) {
                displayNames.push(formattedValue);
            }
        });

        return displayNames.join(', ');
    };

    /**
     *
     * Formats/Serializes an item based on the reference attribute (MultiReference/SingleReference) of the parent item.
     * Note that a filter function has higher precedence than filters array.
     *
     * The options (items) of a Multi/Single Reference attributes are formatted per the filters defined on the attribute.
     * The "filters" are an array of attribute names (let's call them filter attributes) that can be defined as an array or
     * the whole label would be formatted by a filter function.
     *
     * For example, an attribute has the following filters: ['gtin', 'target_market'] then the available items
     * to be referenced should be labelled as: "123456 - Germany".
     *
     * Note that @item parameter can be null in case the filter is called from within Mass Update function.
     */
    const formatReferenceItem = useCallback((item: PersistedItem, parentItem: PersistedItem) => {

        const filters = attribute.params?.filter || attribute.params?.referenceItemAttributes;
        const filterFunctionName = attribute.params?.uiReferenceFilter;

        if (!dataModel || (isEmpty(filterFunctionName) && isEmpty(filters)) || !user || !organization) {
            return item.primaryKey__;
        }

        if (filterFunctionName) {
            const filterFunction = getCustomUiReferenceFilter(filterFunctionName);
            if (!filterFunction) {
                logger.error(`useReferenceItemFormatter: uiReferenceFilter ${filterFunctionName} for attribute ${attribute.name} was not found.`);
            } else {
                return filterFunction(item.primaryKey__, item, parentItem, attribute, user, organization);
            }
        }

        if (!isEmpty(filters)) {
            return formatByFilters(dataModel, item, filters as string[]);
        }

        // We only need one instance of this function.
        // eslint-disable-next-line
    }, []);

    return { formatReferenceItem };
};

export { useReferenceItemFormatter };
